import { markSessionExpired } from "./sessionExpiryNotice";
import axios from 'axios';
import store from '@/store';
import router from "@/router";
import { translate } from './translate';
import authUser from './authUser';
import Cookies from 'js-cookie';
import crsfHandler from "@/composables/csrfHandler";
import apiBaseUrl from '@/composables/apiUrl';
import { attachCorrelationId, captureCorrelationError, captureCorrelationId } from '@/composables/correlationId';
import { appendQueryParameters } from '@/composables/queryParameters.mjs';
import { captureRequestIdentity, sameRequestIdentity } from '@/composables/requestIdentity.mjs';
import { readPublicCatalogue } from '@/composables/publicCatalogues.mjs';

const requestHandler = axios.create({
    baseURL: `${apiBaseUrl}/`,
    headers: {
        'Accept': 'application/json',
    },
    withCredentials: true,
    withXSRFToken: true
});

requestHandler.interceptors.request.use((config) => {
    if (config.jobifullExpectedIdentity && !sameRequestIdentity(config.jobifullExpectedIdentity, captureRequestIdentity(store.getters)))
        throw new axios.CanceledError('The active profile changed before the request was sent.', config);
    attachCorrelationId(config);
    const token = store.getters.getToken; 
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    const activeCompanyId = store.getters.getActiveCompanyId;
    if (activeCompanyId) {
        config.headers['X-Company-Id'] = activeCompanyId;
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

requestHandler.interceptors.request.use(
    config => {
        return config;
      },
      error => {
        return Promise.reject(error);
    }
);

requestHandler.interceptors.response.use(
    response => captureCorrelationId(response),
    error => {
        if (axios.isCancel(error)) return Promise.reject(error);
        captureCorrelationError(error);
        if (error.response == undefined) {
            return {
                success: false,
                data: {},
                message: translate('a problem occurred with your application, but do not worry, we are already looking into it', 'ucfirst'),
                status: 900,
            };
        }else{
            throw error;
        }
    }
);

/**
 * Functions
 */
requestHandler.baseUrl = function getBaseUrl(){
    return requestHandler.defaults.baseURL;
}
requestHandler.makeUrl = function makeUrl(uri){
    return requestHandler.defaults.baseURL + uri;
}
/**
 * Perform an AXIOS request
 * @param {String} url 
 * @param {Object} parameters 
 * @param {String} method - 'post', 'get', 'put', 'patch' or 'delete'
 * @returns {
 *  success: Boolean,
 *  data: requestResult
 *  status: integer
 * }
 */
requestHandler.executeRequest = async function executeRequest(url, parameters = {}, method = 'post', options = {}){
    const originalUrl = url;
    const requestIdentity = options.expectedIdentity || { token: store.getters.getToken, company: store.getters.getActiveCompanyId, role: store.getters.getLoggedAs };
    const axiosConfig = options.bindIdentity || options.expectedIdentity
        ? { ...(options.axiosConfig || {}), jobifullExpectedIdentity: requestIdentity }
        : (options.axiosConfig || {});
    const sameIdentity = () => requestIdentity.token === store.getters.getToken
        && String(requestIdentity.company ?? '') === String(store.getters.getActiveCompanyId ?? '')
        && requestIdentity.role === store.getters.getLoggedAs;
        let resultFormat = {
        success: true,
        data: {},
        message: '',
        status: 200,
        correlationId: null,
    };
    if (typeof FormData !== 'undefined' && parameters instanceof FormData)
        parameters.set('user_lang', store.getters.getUserLang);
    else
        parameters['user_lang'] = store.getters.getUserLang;
    let parametersAsArray = Object.keys(parameters);
    try {
        let response = null;
        switch(method){
            case 'post':
                response = await this.post(url, parameters, axiosConfig);
            break;
            case 'get':
                if(parametersAsArray.length > 0)
                    url = appendQueryParameters(url, parameters);
                response = await this.get(url, axiosConfig);
            break;
            case 'put':
                response = await this.put(url, parameters, axiosConfig);
            break;
            case 'patch':
                response = await this.patch(url, parameters, axiosConfig);
            break;
            case 'delete':
                response = await this.delete(url, {params: parameters, ...axiosConfig});
            break;
        }
        if(response.status == 900){
            store.dispatch('saveSessionMessage', {
                message: translate('a problem occurred with your application, but do not worry, we are already looking into it', 'ucfirst'), 
                messageType: 'error'
            });
            return {
                success: false,
                data: {},
                message: 'error',
                status: 900,
            };
        }
        resultFormat.data = response.data;
        resultFormat.status = response.status;
        resultFormat.correlationId = response.correlationId;
        if(response.data.message !== undefined)
            resultFormat.message = translate(response.data.message);
        return resultFormat;
    } catch (error) {
        if (axios.isCancel(error)) return { success: false, data: {}, status: 499, message: translate('the active profile changed; reload before saving', 'ucfirst') };
        const defaultErrorMessage = translate('unexpected error, please try again later', 'ucfirst');
        resultFormat.success = false;
        resultFormat.data = "";
        resultFormat.status = 500;
        if(error !== undefined && error.response !== undefined){
            resultFormat.data = error.response.data;
            resultFormat.status = error.response.status;
            resultFormat.correlationId = error.correlationId;
            if(error.response.data?.message !== undefined)
                resultFormat.message = translate(error.response.data.message);
            if(resultFormat.status === 419){
                // A rejected CSRF preflight is not evidence that the bearer session expired.
                if(!options.csrfRecoveryAttempted && !options.axiosConfig?.signal?.aborted && sameIdentity()){
                    try {
                        const refreshed = await crsfHandler.getCRSF(true);
                        if(refreshed?.status >= 200 && refreshed.status < 300 && !options.axiosConfig?.signal?.aborted && sameIdentity())
                            return await this.executeRequest(originalUrl, parameters, method, {...options, csrfRecoveryAttempted: true});
                    } catch (_) { /* Keep the original failure and let the user retry explicitly. */ }
                }
                resultFormat.message = translate('could not validate the request; please try again', 'ucfirst');
                return resultFormat;
            }
        }
        if(resultFormat.message == '')
            resultFormat.message = defaultErrorMessage;
        if(resultFormat.status == 401 && resultFormat.data?.message == 'Unauthenticated.'){
            if(options.handleUnauthorized !== false){
                await this.expireSession();
            }else if(!store.getters.isLoggedIn){
                Cookies.remove('XSRF-TOKEN');
                Cookies.remove('jobifull_session');
                Cookies.remove('last_login_as');
                store.dispatch('cleanAuthData');
            }
        }
        return resultFormat;
    }
}
let sessionExpirationPromise = null;
requestHandler.expireSession = async function expireSession(message = translate('authentication expired')){
    if(sessionExpirationPromise)
        return sessionExpirationPromise;
    sessionExpirationPromise = (async () => {
        const hadSession = Boolean(store.getters.getToken || store.getters.isLoggedIn);
        await authUser.logout(true);
        if(hadSession){
            markSessionExpired();
            store.dispatch('saveSessionMessage', {
                message,
                messageType: 'error'
            });
        }
        if(router.currentRoute.value.path !== '/login')
            await router.replace({path: '/login'});
    })();
    return sessionExpirationPromise;
}
requestHandler.resetSessionExpiration = function resetSessionExpiration(){
    sessionExpirationPromise = null;
}
requestHandler.redirect = async function goTo(url = '/'){
    return router.push({path: url});
}
requestHandler.replace = async function replaceWith(url = '/'){
    return router.replace({path: url});
}
requestHandler.redirectByProfile = async function redirectByProfile(){
    const routeToGo = store.getters.isLoggedIn && store.getters.isOnlyPersonProfile ? '/dashboard/profile' : '/dashboard/' + store.getters.getLoggedAs + '/' + store.getters.getUserSlug;
    requestHandler.redirect(routeToGo);
}
requestHandler.fetchOnPublicData = readPublicCatalogue;

export default requestHandler;
